import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ScenarioConfig, ScenarioCredential, ScenarioIssue } from './types';
import { moveItem, newConnection, omitKeys, removeAt, renameConnectionAt, setIn } from './scenarioDraft';
import { AddButton, IssueList, ItemRow, NameField, RowActions, SelectField, TextField } from './ui';

interface Props {
  config: ScenarioConfig;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
  credentials: ScenarioCredential[];
}

export const ConnectionsEditor: React.FC<Props> = ({ config, onChange, issues, credentials }) => {
  const { t } = useTranslation();
  const credentialOptions = [
    { value: '', label: t('agents.scenario.noAuthentication') },
    ...credentials.map(c => ({ value: c.ref, label: `${c.ref} (${c.header_name})` })),
  ];

  return (
    <div className="space-y-3">
      <IssueList issues={issues} pointer="/connections" />
      {config.connections.map((connection, index) => {
        const ptr = `/connections/${index}`;
        return (
          <ItemRow
            key={index}
            className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(140px,1fr)_minmax(240px,2fr)_minmax(180px,1fr)_auto]"
          >
            <NameField
              label={t('agents.scenario.technicalName')}
              value={connection.id}
              onCommit={value => onChange(renameConnectionAt(config, index, value))}
              issues={issues}
              pointer={`${ptr}/id`}
            />
            <TextField
              label={t('agents.scenario.baseUrl')}
              value={connection.base_url}
              onChange={value => onChange(setIn(config, ['connections', index, 'base_url'], value))}
              issues={issues}
              pointer={`${ptr}/base_url`}
              placeholder="https://api.example.com"
              monospace
            />
            <SelectField
              label={t('agents.scenario.credential')}
              value={connection.credential_ref ?? ''}
              options={credentialOptions}
              onChange={value => {
                const rest = omitKeys(connection, 'credential_ref');
                const next = value ? { ...rest, credential_ref: value } : rest;
                onChange(setIn(config, ['connections', index], next));
              }}
              issues={issues}
              pointer={`${ptr}/credential_ref`}
            />
            <div className="self-end pb-1">
              <RowActions
                index={index}
                count={config.connections.length}
                onMove={(from, to) => onChange({ ...config, connections: moveItem(config.connections, from, to) })}
                onRemove={() => onChange({ ...config, connections: removeAt(config.connections, index) })}
              />
            </div>
            <div className="md:col-span-4">
              <IssueList issues={issues} pointer={ptr} />
            </div>
          </ItemRow>
        );
      })}
      <AddButton
        label={t('agents.scenario.addConnection')}
        onClick={() => onChange({ ...config, connections: [...config.connections, newConnection(config)] })}
      />
    </div>
  );
};
